/**
 * R26 QA — the thread-list traversal that powers auto-advance (after triage) and
 * the J/K shortcuts, extracted from App so it can be tested directly. App reads
 * cached React Query data for the active view (the inbox renders an infinite query
 * `{pages:[{items}]}`; follow-ups a flat `{items}`); these pure helpers flatten
 * that and pick the adjacent id without needing a full e2e render.
 */
export type ThreadCache =
  | { pages?: Array<{ items?: Array<{ id: string }> }> }
  | { items?: Array<{ id: string }> }
  | undefined;

/** Flatten a cached infinite-query (pages) or flat-list (items) into thread ids. */
export function flattenCachedThreadIds(data: ThreadCache): string[] {
  if (!data) return [];
  const items =
    'pages' in data && Array.isArray(data.pages)
      ? data.pages.flatMap((p) => (Array.isArray(p?.items) ? p.items : []))
      : 'items' in data && Array.isArray(data.items)
        ? data.items
        : [];
  return items.map((t) => t?.id).filter((id): id is string => typeof id === 'string');
}

/**
 * The id `delta` steps from `currentId` in `ids`.
 *   - empty list → null
 *   - no current selection → the first (delta>0) / last (delta<0) id
 *   - current not present, or the step falls off either end → null
 * `+1` from the just-triaged row IS the auto-advance "next" (null = deselect at end).
 */
export function adjacentThreadId(
  ids: string[],
  currentId: string | null,
  delta: 1 | -1,
): string | null {
  if (ids.length === 0) return null;
  if (currentId === null) return delta > 0 ? (ids[0] ?? null) : (ids[ids.length - 1] ?? null);
  const idx = ids.indexOf(currentId);
  if (idx < 0) return null;
  const next = idx + delta;
  return next >= 0 && next < ids.length ? (ids[next] ?? null) : null;
}
